import { useState, useCallback } from 'react';
import { Organization } from '../types';
import { useCRMContext } from '../CRMContext';
import { apiFetch } from '../../../api/core';


export const useOrganizationMerge = () => {
  const [targetOrganization, setTargetOrganization] = useState<Organization | null>(null);
  const [sourceOrganizations, setSourceOrganizations] = useState<Organization[]>([]);
  const [isMerging, setIsMerging] = useState(false);

  const { apiPath } = useCRMContext();

  // 1. Start the merge process by selecting the initial target
  const startMerge = useCallback((org: Organization) => {
    setTargetOrganization(org);
    setSourceOrganizations([]); // Reset sources if starting a new merge
  }, []);

  // 2. Add an organization to the source list (to be merged INTO the target)
  const addSource = useCallback((org: Organization) => {
    // Prevent adding if it's already the target
    if (targetOrganization?._id === org._id) return;
    
    // Prevent adding duplicate sources
    setSourceOrganizations(prev => {
      if (prev.some(s => s._id === org._id)) return prev;
      return [...prev, org];
    });
  }, [targetOrganization]);

  // 3. Remove a specific source from the list
  const removeSource = useCallback((orgId: string) => {
    setSourceOrganizations(prev => prev.filter(s => s._id !== orgId));
  }, []);

  // 4. Swap a source to become the new target (the old target becomes a source)
  const makeTarget = useCallback((newTarget: Organization) => {
    setTargetOrganization(prevTarget => {
      if (!prevTarget) return newTarget; // Edge case fallback
      
      setSourceOrganizations(prevSources => {
        // Remove the new target from sources, and add the old target to sources
        const filteredSources = prevSources.filter(s => s._id !== newTarget._id);
        return [...filteredSources, prevTarget];
      });
      
      return newTarget;
    });
  }, []);

  // 5. Cancel and completely close the merge panel
  const cancelMerge = useCallback(() => {
    setTargetOrganization(null);
    setSourceOrganizations([]);
    setIsMerging(false);
  }, []);

  // Derived state for the UI
  const isPanelVisible = targetOrganization !== null;
  const canSubmitMerge = targetOrganization !== null && sourceOrganizations.length > 0;

  // 6. Action to execute the merge
  const executeMerge = useCallback(async () => {
    if (!canSubmitMerge) return;

    setIsMerging(true);
    try {
      await apiFetch(`${apiPath}/organization/merge`, "post", {
        target_org_id: targetOrganization._id,
        source_org_ids: sourceOrganizations.map(s => s._id),
      })
      
      // Clear state on success
      cancelMerge();
    } finally {
      setIsMerging(false);
    }
  }, [targetOrganization, sourceOrganizations, canSubmitMerge, cancelMerge, apiPath]);

  return {
    // State
    targetOrganization,
    sourceOrganizations,
    isMerging,
    
    // Derived UI flags
    isPanelVisible,
    canSubmitMerge,
    
    // Actions
    startMerge,
    addSource,
    removeSource,
    makeTarget,
    cancelMerge,
    executeMerge,
  };
};

export type OrganizationMergeData = ReturnType<typeof useOrganizationMerge>;